// Story 8.5 (ADO-2014), D-20: the landing page's "Next cut-off" line and list, from the
// shared per-cart answers (`useCutOffStore`). Pure: no React.
//
//   const next = nextCutOffs(depots, views);   // null when no depot has a deadline
//
// - One row per cart (the depot, the account, Story 8.4 ADO-2013, so each Value Centre account
//   is a row of its own, and the entry kind, Enabler E8-G ADO-3109 DA-8, so Chill and Fruit & Veg
//   are two rows), under the first name given for it. The answer is read per depot and account
//   (`cutOffKey`), so Chill and Fruit & Veg read the same one.
// - A depot is listed when its answer has a deadline to count down to (`countdownDeadline`:
//   OPEN's deadline, or PASSED's next one, D-14). NONE, PASSED with no next deadline and a
//   depot with no answer yet (loading or failed) are left out.
// - Rows are soonest first; depots with the same deadline keep the order given.
// - `soonest` is every row sharing the first row's exact deadline: the line names them all
//   ("VC Dublin and WOW") and "N more" counts the rest.

import { cartAccountKey, cartTargetKey } from '../api/cart-target';
import type { OrderingEntryKind } from '../api/ordering-choices.client';
import { countdownDeadline, type CutOffDisplayStatus } from '../shared/cutOffDisplay';

export interface CutOffDepot {
  readonly depotId: string;
  /** The account the cut-off is read for; absent or `null` for none. */
  readonly customerAccountCode?: string | null;
  /** The kind of the entry the cart belongs to; absent reads as `DEPOT` (Enabler E8-G,
   *  ADO-3109, DA-8). It tells two carts apart, not two reads. */
  readonly entryKind?: OrderingEntryKind;
  readonly name: string;
}

export interface NextCutOffRow {
  readonly depotId: string;
  readonly customerAccountCode?: string | null;
  readonly entryKind?: OrderingEntryKind;
  readonly name: string;
  /** The deadline counted down to, ISO 8601 UTC. */
  readonly deadline: string;
}

export interface NextCutOffs {
  /** Every row, soonest first. */
  readonly rows: readonly NextCutOffRow[];
  /** The leading rows that share the soonest exact deadline (at least one). */
  readonly soonest: readonly NextCutOffRow[];
}

export function nextCutOffs(
  depots: readonly CutOffDepot[],
  views: Readonly<Record<string, { readonly status: CutOffDisplayStatus } | undefined>>,
): NextCutOffs | null {
  const seen = new Set<string>();
  const rows: { row: NextCutOffRow; deadlineMs: number }[] = [];
  for (const depot of depots) {
    const rowKey = cutOffRowKey(depot);
    if (seen.has(rowKey)) continue;
    seen.add(rowKey);
    const deadline = countdownDeadline(views[cutOffKey(depot)]?.status ?? null);
    const deadlineMs = deadline === null ? Number.NaN : Date.parse(deadline);
    if (deadline === null || Number.isNaN(deadlineMs)) continue;
    rows.push({ row: { ...depot, deadline }, deadlineMs });
  }
  // Array.prototype.sort is stable, so equal deadlines keep the order given.
  rows.sort((left, right) => left.deadlineMs - right.deadlineMs);
  const first = rows[0];
  if (first === undefined) return null;
  return {
    rows: rows.map((entry) => entry.row),
    soonest: rows
      .filter((entry) => entry.deadlineMs === first.deadlineMs)
      .map((entry) => entry.row),
  };
}

/** The key a row's cut-off answer is stored under (`cartAccountKey`: the depot and account). */
export function cutOffKey(depot: Pick<CutOffDepot, 'depotId' | 'customerAccountCode'>): string {
  return cartAccountKey({
    depotId: depot.depotId,
    customerAccountCode: depot.customerAccountCode ?? null,
  });
}

/** The key of a row: its cart (`cartTargetKey`), so Chill and Fruit & Veg are two rows. */
export function cutOffRowKey(
  depot: Pick<CutOffDepot, 'depotId' | 'customerAccountCode' | 'entryKind'>,
): string {
  return cartTargetKey({
    depotId: depot.depotId,
    customerAccountCode: depot.customerAccountCode ?? null,
    entryKind: depot.entryKind ?? 'DEPOT',
  });
}

/** "VC Dublin", "VC Dublin and WOW", "VC Dublin, WOW and Corrib Retail". */
export function joinDepotNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
